import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  TemplateRef,
  untracked,
  viewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatPaginator, MatPaginatorModule, MatPaginatorSelectConfig, PageEvent } from '@angular/material/paginator';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { EVENT_CELL_COLUMN_DEF, SELECT_CELL_COLUMN_DEF } from './table.constants';
import { SelectionModel } from '@angular/cdk/collections';
import { TABLE_SELECTOR_HANDLER, TableSelectionHandler } from './table-selection-handler';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { ToDisplayedColumnsPipe } from './to-displayed-columns.pipe';
import { CommonModule } from '@angular/common';
import { SortDataTransformer, TableColumn } from './table.model';
import { CellRendererComponent } from './cell-renderer/cell-renderer.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TableRowDisabledPipe } from './table-row-disabled/table-row-disabled.pipe';
import { RowColumnTooltipPipe } from './row-column-tooltip/row-column-tooltip.pipe';

@Component({
  selector: 'cbre-table',
  templateUrl: './table.component.html',
  styleUrl: './table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatTableModule,
    MatPaginatorModule,
    MatCheckboxModule,
    MatSortModule,
    CellRendererComponent,
    MatTooltipModule,
    ToDisplayedColumnsPipe,
    TableRowDisabledPipe,
    RowColumnTooltipPipe,
  ],
})
export class TableComponent<T = Record<string, any>> implements OnInit, AfterViewInit, OnDestroy {
  public readonly EVENT_CELL_COLUMN_DEF = EVENT_CELL_COLUMN_DEF;
  public readonly SELECT_CELL_COLUMN_DEF = SELECT_CELL_COLUMN_DEF;

  /**
   * Selection object
   */
  public selection: SelectionModel<T> = null;

  /**
   * Table data source
   */
  public dataSource: MatTableDataSource<T> = null;

  /**
   * Dictionary of data transformer functions to be used by the DataSource
   */
  private sortTransformerMap: Record<string, SortDataTransformer> = {};

  // --- Signal Inputs ---
  readonly tableId = input('cbre_');
  readonly elements = input<T[]>([]);
  readonly columns = input<TableColumn[]>([]);
  readonly toolboxLeftTpl = input<TemplateRef<any>>(null);
  readonly toolboxRightTpl = input<TemplateRef<any>>(null);
  readonly cellRendererTpl = input<TemplateRef<any>>(null);
  readonly cellEventsTpl = input<TemplateRef<any>>(null);
  readonly headerEventsTpl = input<TemplateRef<any>>(null);
  readonly sort = input<{ sortBy?: string }>();
  readonly selectionMode = input<'single' | 'multi' | null>(null);
  readonly hidePaginator = input(false);
  readonly showFirstLastButtons = input(false);
  readonly noDataLabel = input('No data available');
  readonly pageSizeOptions = input([10, 25, 50]);
  readonly totalCount = input<number>(null);
  readonly pageSize = input<number>(null);
  readonly maxHeight = input('inherit');
  readonly paginatorSelectConfig = input<MatPaginatorSelectConfig>({
    panelClass: 'cbre-table-paginator',
    disableOptionCentering: true,
  });
  readonly showPaginatorSelectedValueInClass = input(false);
  readonly stickyHeader = input(false);
  readonly isRowClickable = input(false);

  // --- Signal Outputs ---
  readonly sortChange = output<Sort>();
  readonly pageChange = output<PageEvent>();
  readonly rowClick = output<T>();

  // --- Signal ViewChild/Queries ---
  readonly paginator = viewChild(MatPaginator);
  readonly sortOptions = viewChild(MatSort);

  public selectionHandler = inject<TableSelectionHandler<Record<string, any>>>(TABLE_SELECTOR_HANDLER);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    // Effect: react to elements changes → rebuild dataSource
    effect(() => {
      const elements = this.elements();
      this.dataSource = new MatTableDataSource<T>(elements);
      // Set sorting configurations when the sorting is handled within the table
      if (this.dataSource && Object.keys(this.sortTransformerMap).length > 0) {
        this.cdr.detectChanges();
        const sortOpts = untracked(() => this.sortOptions());
        this.dataSource.sort = sortOpts;

        this.dataSource.sortingDataAccessor = (dataObject: Record<string, any>, field: string) => {
          const transformer = this.sortTransformerMap[field];
          const val = dataObject[field];
          return transformer != null ? transformer(val) : val;
        };
      }

      if (untracked(() => this.totalCount()) == null) {
        this.dataSource.paginator = untracked(() => this.paginator());
      }
    });

    // Effect: react to totalCount changes → remove paginator from dataSource
    effect(() => {
      const tc = this.totalCount();
      if (tc != null && this.dataSource != null) {
        this.dataSource.paginator = null;
      }
    });
  }

  ngOnInit() {
    this.selection = new SelectionModel<T>(this.selectionMode() === 'multi', []);
    this.selectionHandler.configure(this.dataSource, this.selection);
  }

  ngAfterViewInit() {
    if (this.dataSource) {
      this.dataSource.sort = this.sortOptions();
      if (this.totalCount() == null) {
        this.dataSource.paginator = this.paginator();
      }
    }

    const cols = this.columns();
    if (cols?.some(column => column.sortDataTransformerFn != null)) {
      // generate transformer map
      this.sortTransformerMap = cols.reduce((acc, column) => {
        const { sortDataTransformerFn, field } = column;
        acc[field] = sortDataTransformerFn;
        return acc;
      }, {});
    }

    this.sortOptions()?.sortChange?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(evt => this.sortChange.emit(evt));
  }

  ngOnDestroy(): void {
    // Cleanup handled by @UntilDestroy()
  }

  selectAllToggle() {
    this.selectionHandler.selectAllToggle();
  }

  selectionToggle(row: Record<string, any>) {
    this.selectionHandler.selectionToggle(row);
  }

  get isAllSelected() {
    return this.selection?.hasValue() && this.selectionHandler?.isAllSelected();
  }

  get isIndeterminate() {
    return this.selection?.hasValue() && !this.selectionHandler?.isAllSelected();
  }

  checkboxLabel(row?: T): string {
    return this.selectionHandler.checkboxLabel(row);
  }

  trackColumn(column: TableColumn): number | string {
    return column.columnId;
  }

  handleRowClick(row: T) {
    if (this.isRowClickable()) {
      this.rowClick.emit(row);
    }
  }
}
